import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { TextField, MenuItem, InputAdornment, Tooltip, Button, Typography, Box, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { Add, DeleteForeverOutlined } from '@mui/icons-material';
import { getRepositoryList, deleteRepository } from '../../apis/repository';
import StatusBadge from '../StatusBadge';
import CreateRepositoryDialog from './CreateRepositoryDialog';
import RepositoryTypeDisplay from './RepositoryTypeDisplay';
import { useDebounce } from '../../hooks/useDebounce';
import { useSelector } from 'react-redux';
import { first, includes, isEmpty } from 'lodash';
import { formatDate } from '../../lib/utils';
import useSubscription from '../../hooks/useSubscription';
import { Navigate } from 'react-router-dom';
import ConfirmationDialog from '../dashboard/ConfirmationDialog';
import { CLOUD_STORAGE_TYPE } from '../../configs/constant';
import TablePagination from '../TablePagination';

const DELETE_CONFIRM_DIALOG_INITIAL_STATE = {
    open: false,
    type: 'danger',
    title: '',
    message: '',
    onConfirm: null,
    loading: false,
};

const RepositoryList = ({ pageSize = 5 }) => {
    const permissions = useSelector((state) => state.auth.permissions);

    const [repositories, setRepositories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, _setStatusFilter] = useState('all');
    const [totalCount, setTotalCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({
        pageSize,
        page: 0,
    });
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [sortModel, setSortModel] = React.useState(null);
    const [confirmDialog, setConfirmDialog] = useState(DELETE_CONFIRM_DIALOG_INITIAL_STATE);

    const debouncedSearch = useDebounce(search, 500);

    useEffect(() => {
        loadRepositories();
    }, [paginationModel.page, paginationModel.pageSize, debouncedSearch, typeFilter, statusFilter, sortModel]);

    const loadRepositories = async () => {
        if (!isPermitted('list')) return;
        setLoading(true);
        try {
            const response = await getRepositoryList(
                paginationModel.page + 1,
                paginationModel.pageSize,
                search,
                typeFilter,
                statusFilter,
                sortModel
            );
            setRepositories(response.results || []);
            setTotalCount(response.count || 0);
        } catch (error) {
            toast.error('Failed to load repositories');
            setRepositories([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRepository = async (repoId, repoName) => {
        try {
            setConfirmDialog({
                open: true,
                type: 'danger',
                title: 'Delete Repository',
                message: (
                    <Typography>
                        Are you sure you want to delete this repository <strong>{repoName}</strong>?
                    </Typography>
                ),
                onConfirm: () => confirmDeleteRepository(repoId),
                loading: false,
            });
        } catch (error) {
            toast.error('Failed to delete repository');
        }
    };

    const confirmDeleteRepository = async (repoId) => {
        try {
            setConfirmDialog((prev) => ({ ...prev, loading: true }));
            await deleteRepository(repoId);
            setRepositories((prev) => prev.filter((repo) => repo.id !== repoId));
            setTotalCount((prev) => prev - 1);
            toast.success('Repository deleted successfully');
            setConfirmDialog(DELETE_CONFIRM_DIALOG_INITIAL_STATE);
        } catch (error) {
            toast.error('Failed to delete repository');
        } finally {
            setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
    };

    const handleCreateRepository = () => setCreateDialogOpen(true);

    const handleCreateSuccess = () => loadRepositories();

    const isPermitted = (value) => includes(permissions?.repositories, value);

    const columns = [
        {
            field: 'name',
            headerName: 'Repository Name',
            flex: 2.5,
            renderCell: (params) => params?.value || '-',
        },
        {
            field: 'type',
            headerName: 'Repository Type',
            flex: 2,
            renderCell: (params) => <RepositoryTypeDisplay type={params?.value} />,
        },
        {
            field: 'created_at',
            headerName: 'Created',
            flex: 1,
            valueFormatter: formatDate,
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => {
                const status = params?.value || '-';
                return <StatusBadge status={status} />;
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const repository = params?.row;
                if (!repository) return null;

                return (
                    <Box>
                        {/* Delete */}
                        {isPermitted('delete') ? (
                            <Tooltip title="Delete Repository">
                                <IconButton
                                    onClick={() => handleDeleteRepository(repository?.id, repository?.name)}
                                    color="error"
                                >
                                    <DeleteForeverOutlined />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </Box>
                );
            },
        },
    ];

    // Note: Client-side filtering removed since we're using server-side pagination
    // Filters should be sent to the API for better performance with large datasets

    const { isTrial, isActive, canUploadCourse } = useSubscription();

    // Redirect to pricing if Trial or inactive subscription
    if (isTrial || !isActive) {
        return <Navigate to="/pricing" replace />;
    }

    const handleSortModelChange = async (sortOption) => {
        if (isEmpty(sortOption)) {
            setSortModel(null);
            return;
        }

        const { field, sort } = first(sortOption);
        setSortModel({ field, sort });
    };

    return (
        <div className="custom_table_container">
            <Box variant="div" className="table_filters">
                <Typography variant="h6" fontWeight={600}>
                    Repositories
                </Typography>
                <Box className="filter_box">
                    <TextField
                        size="small"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <span className="material-symbols-rounded">search</span>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <TextField
                        select
                        label="Filter by type"
                        size="small"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        style={{ minWidth: 180 }}
                    >
                        <MenuItem value="all">All Types</MenuItem>
                        {CLOUD_STORAGE_TYPE?.map((cloud) => (
                            <MenuItem value={cloud.type} key={cloud.type}>
                                {cloud.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    {/* <TextField
                        select
                        label="Filter by status"
                        size="small"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{ minWidth: 180 }}
                    >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="approved">Validated</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="processing">Processing</MenuItem>
                    </TextField> */}
                    {isPermitted('add') ? (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleCreateRepository}
                            disabled={!canUploadCourse}
                        >
                            Add Repository
                        </Button>
                    ) : null}
                </Box>
            </Box>

            <Box variant="div" className="custom_table_responsive">
                <DataGrid
                    rows={repositories}
                    columns={columns}
                    getRowId={(row) => row.id}
                    autoHeight
                    pageSizeOptions={[5, 10, 25, 50]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pagination
                    paginationMode="server"
                    loading={loading}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    localeText={{
                        noRowsLabel: 'No Repositories Found',
                        sortLabel: '',
                    }}
                    rowCount={totalCount}
                    sortingMode="server"
                    onSortModelChange={handleSortModelChange}
                    slots={{
                        pagination: () => (
                            <TablePagination
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                totalCount={totalCount}
                                pageSizeOptions={[5, 10, 25, 50]}
                            />
                        ),
                    }}
                />
            </Box>

            <ConfirmationDialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog(DELETE_CONFIRM_DIALOG_INITIAL_STATE)}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                type={confirmDialog.type}
                loading={confirmDialog.loading}
                confirmText={'Delete'}
            />

            {/* Create Repository Dialog */}
            <CreateRepositoryDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSuccess={handleCreateSuccess}
            />
        </div>
    );
};

export default RepositoryList;
